import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getArtistDiscography,
  getBandDiscography,
  type Discography as DiscographyData,
  type DiscographyEntry,
} from "../../infra/api.js";
import type { AxisDelta } from "../../domain/index.js";
import { SonicTraitsCard } from "./SonicTraitsCard.js";

interface Props {
  /** Whose discography — a band or an artist. Drives which endpoint is hit. */
  subject: { kind: "band"; id: string } | { kind: "artist"; id: string };
}

/**
 * A vertical chronological timeline of a band's or an artist's songs — title,
 * date, an audio indicator, and the effective sonic-trait chips per entry — with
 * a style-evolution summary at the top (first → last, with the highlighted
 * diff). Reads as a career sheet / encarte.
 *
 * NEVER groups songs into "albums" (spec §12 #12) — it is a flat chronological
 * list.
 */
export function Discography({ subject }: Props): JSX.Element {
  const [data, setData] = useState<DiscographyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    const fetch =
      subject.kind === "band"
        ? getBandDiscography(subject.id, ctrl.signal)
        : getArtistDiscography(subject.id, ctrl.signal);
    fetch
      .then(({ discography }) => {
        setData(discography);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [subject.kind, subject.id]);

  if (loading) {
    return (
      <p className="font-mono text-[11px] uppercase tracking-widest2 text-ink-dim">
        carregando trajetória…
      </p>
    );
  }

  if (error) {
    return (
      <p
        role="alert"
        className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
      >
        {error}
      </p>
    );
  }

  if (!data || data.entries.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 border border-dashed border-border p-8 text-center">
        <p className="font-display text-xl text-paper">Sem trajetória ainda</p>
        <p className="font-mono text-[11px] uppercase tracking-widest2 text-ink-muted">
          {subject.kind === "band"
            ? "esta banda não gravou nenhuma faixa"
            : "este personagem ainda não tocou em nenhuma faixa"}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {data.evolution && (
        <EvolutionSummary
          first={data.evolution.first}
          last={data.evolution.last}
          delta={data.evolution.delta}
        />
      )}
      <ol className="flex flex-col">
        {data.entries.map((entry, i) => (
          <TimelineEntry
            key={entry.project_id}
            entry={entry}
            index={i}
            total={data.entries.length}
          />
        ))}
      </ol>
    </div>
  );
}

/** The style-evolution panel — the first vs. last trait-bearing entry + diff. */
function EvolutionSummary({
  first,
  last,
  delta,
}: {
  first: DiscographyEntry["traits"];
  last: DiscographyEntry["traits"];
  delta: AxisDelta;
}): JSX.Element {
  const moved = delta.added.length + delta.removed.length + delta.changed.length;
  return (
    <div className="border border-border bg-asphalt/60 p-4" aria-label="Evolução de estilo">
      <p className="catalog-num mb-3 text-oscillo">EVOLUÇÃO DE ESTILO</p>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-4">
        <div>
          <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
            no começo
          </p>
          <SonicTraitsCard traits={first} emptyHint="sem traços" />
        </div>
        <div className="self-center font-mono text-lg text-ink-dim" aria-hidden>
          →
        </div>
        <div>
          <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
            hoje
          </p>
          <SonicTraitsCard traits={last} emptyHint="sem traços" />
        </div>
      </div>
      <div className="mt-3 border-t border-border pt-3">
        {moved === 0 ? (
          <p className="font-mono text-[11px] italic text-ink-dim">
            o estilo se manteve constante ao longo da trajetória
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {delta.added.map((d) => (
              <DiffChip key={`a-${d}`} kind="added" text={d} />
            ))}
            {delta.removed.map((d) => (
              <DiffChip key={`r-${d}`} kind="removed" text={d} />
            ))}
            {delta.changed.map((d) => (
              <DiffChip key={`c-${d}`} kind="changed" text={d} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** One song on the timeline — number, title, date, audio indicator, traits. */
function TimelineEntry({
  entry,
  index,
  total,
}: {
  entry: DiscographyEntry;
  index: number;
  total: number;
}): JSX.Element {
  const last = index === total - 1;
  return (
    <li className="grid grid-cols-[40px_1fr] gap-3">
      {/* The timeline rail — a node + a connecting line. */}
      <div className="flex flex-col items-center">
        <span
          className={`mt-3 h-2.5 w-2.5 shrink-0 border ${
            entry.has_audio ? "border-butter bg-butter" : "border-border bg-tape"
          }`}
          aria-hidden
        />
        {!last && <span className="w-px flex-1 bg-border" aria-hidden />}
      </div>

      <Link
        to={`/songs/${entry.project_id}`}
        className="group mb-3 flex flex-col gap-2 border border-border bg-tape p-4 transition-colors hover:border-borderHi hover:bg-tapeElev"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span
            className="font-display text-xl leading-tight text-paper group-hover:text-butter"
            style={{ fontVariationSettings: '"opsz" 36, "wght" 500, "SOFT" 60' }}
          >
            {String(index + 1).padStart(2, "0")} ·{" "}
            {entry.title ?? <em className="text-ink-muted">sem título</em>}
          </span>
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest2 tabular-nums text-ink-dim">
            {new Date(entry.created_at).toLocaleDateString("pt-BR")}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest2 ${
              entry.has_audio ? "border-butter text-butter" : "border-border text-ink-dim"
            }`}
          >
            {entry.has_audio ? "● com áudio" : "○ sem áudio"}
          </span>
          {entry.title_status === "pending" && (
            <span className="font-mono text-[9px] uppercase tracking-widest2 text-oscillo">
              gerando título…
            </span>
          )}
        </div>

        <SonicTraitsCard traits={entry.traits} emptyHint="faixa sem traços efetivos" />
      </Link>
    </li>
  );
}

function DiffChip({
  kind,
  text,
}: {
  kind: "added" | "removed" | "changed";
  text: string;
}): JSX.Element {
  const styles: Record<typeof kind, string> = {
    added: "border-moss bg-moss/15 text-moss",
    removed: "border-vinyl bg-vinyl/15 text-vinyl",
    changed: "border-butter bg-butter/15 text-butter",
  };
  const verb: Record<typeof kind, string> = {
    added: "adicionado",
    removed: "removido",
    changed: "alterado",
  };
  return (
    <span
      aria-label={`${verb[kind]}: ${text}`}
      className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] lowercase tracking-widest2 ${styles[kind]}`}
    >
      {text}
    </span>
  );
}
